import {
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import type { EmailIntegration, Queue } from 'lib-shared-legacy-types';
import { PureComponent, ReactElement } from 'react';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import SelectField from '../../../shared/util/SelectField';
import type { ContactFormModalStatePropsT } from '../../containers/modals/contactform';
import type {
  FlowChatNodeContactFormPayloadT,
  FlowChatNodeContactFormT,
} from '../nodeTypes';

type PropsT = FlowManagerEditModalComponentT<FlowChatNodeContactFormT> &
  ContactFormModalStatePropsT;

type InjectedProps = InjectedFormProps<FlowChatNodeContactFormT, PropsT>;

const validate = (node: FlowChatNodeContactFormT): FormErrors => {
  let errors = {};
  if (!node) errors;

  if (
    !node.payload ||
    !node.payload.queue ||
    !node.payload.queue.value ||
    node.payload.queue.value === ''
  ) {
    errors = {
      payload: {
        queue: {
          value: 'Required - please select a queue',
        },
      },
    };
  }

  return errors;
};

class ContactFormModal extends PureComponent<PropsT & InjectedProps> {
  @boundMethod
  handleSaveClick(values: FlowChatNodeContactFormT): void {
    let selectedQueue;
    if (values.payload?.queue?.value) {
      const matchingQueue = this.props.queuesState.queues.find(
        (queue: Queue) => queue.id === values.payload.queue.value,
      );

      if (matchingQueue) {
        selectedQueue = {
          value: matchingQueue.id,
          label: matchingQueue.name,
        };
      }
    }

    const payload: FlowChatNodeContactFormPayloadT = {
      queue: selectedQueue,
    };

    if (values.payload.fromEmail) {
      payload.fromEmail = values.payload.fromEmail;
    }

    const nodeOptionsText = `Contact Form ${
      selectedQueue.label ? `(${selectedQueue.label})` : ''
    }`;

    const nodeOptions: FlowChatNodeContactFormT = {
      ...this.props.node,
      ...values,
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.CONTACT_FORM,
      overlay: [],
      payload,
      text: nodeOptionsText,
    };

    this.props.replaceNode(nodeOptions);
    this.props.triggerClose();
  }

  @boundMethod
  closeModal() {
    const { destroy, triggerClose } = this.props;
    destroy();
    triggerClose();
  }

  render() {
    const {
      fromEmails,
      handleSubmit,
      node,
      nodeLabel,
      queuesState,
      dirty: isChanged,
    } = this.props;

    const listQueues = (queue: Queue): ReactElement => {
      return (
        <option key={queue.id} value={queue.id}>
          {queue.name}
        </option>
      );
    };

    const listFromMails = (
      fromEmail: EmailIntegration,
      index: number,
    ): ReactElement => {
      const fromEmailAddress =
        fromEmail.spf_sender_email !== undefined &&
        fromEmail.spf_sender_email !== null &&
        typeof fromEmail.spf_sender_email === 'string'
          ? fromEmail.spf_sender_email
          : fromEmail.email_address;
      const label =
        fromEmail.sender_name !== undefined && fromEmail.sender_name !== null
          ? `${fromEmail.sender_name} <${fromEmailAddress}>`
          : fromEmailAddress;
      return (
        <option value={fromEmail.email_address} key={index}>
          {label}
        </option>
      );
    };

    return (
      <div className="contact-form modal-chatflow-holder">
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalHead title={nodeLabel} onClose={this.closeModal} />

          <ModalBody>
            <FlexBox flexDirection="column">
              <Field
                displayName="Queue"
                required={true}
                name="payload.queue.value"
                className="form-control"
                component={SelectField}
              >
                <option hidden value="">
                  {' '}
                  - Please select a queue
                </option>
                {queuesState.queues
                  .filter(
                    (queue) =>
                      queue.id === node.payload?.queue?.value ||
                      !queue.is_archived,
                  )
                  .map(listQueues)}
              </Field>

              <Field
                displayName="Email"
                name="payload.fromEmail"
                className="form-control mb0"
                component={SelectField}
              >
                <option value=""> - Select a from email</option>
                {fromEmails.map(listFromMails)}
              </Field>
            </FlexBox>
          </ModalBody>

          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            confirmProps={{ disabled: !isChanged }}
            cancelOnClick={this.closeModal}
          />
        </Form>
      </div>
    );
  }
}

export default reduxForm<FlowChatNodeContactFormT, PropsT>({
  form: 'chatFlowContactForm', // a unique identifier for this form
  enableReinitialize: true,
  validate,
})(ContactFormModal);
